import axios, { AxiosError } from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

/** Share GET transport work only while a matching request is in flight. */
export function deduplicateGetRequests(client: AxiosInstance): void {
    const adapter = axios.getAdapter(client.defaults.adapter);
    const pending = new Map<string, Promise<AxiosResponse>>();
    const functionIds = new WeakMap<Function, number>();
    let nextFunctionId = 0;

    const requestKey = (config: InternalAxiosRequestConfig): string => JSON.stringify({
        ...config,
        url: client.getUri(config),
        params: undefined,
        paramsSerializer: undefined,
        headers: Object.entries(config.headers.toJSON())
            .map(([name, value]) => [name.toLowerCase(), value])
            .sort(([left], [right]) => String(left).localeCompare(String(right)))
    }, (_, value) => {
        // Different callbacks (including validateStatus) must not share transport work.
        if (typeof value === 'function') {
            if (!functionIds.has(value)) functionIds.set(value, ++nextFunctionId);
            return { functionId: functionIds.get(value) };
        }
        return value;
    });

    client.defaults.adapter = async config => {
        if (config.method !== 'get') {
            // A read after a write must not reuse a response from before that write.
            pending.clear();
            try {
                return await adapter(config);
            } finally {
                pending.clear();
            }
        }

        // Cancellation, progress callbacks and streams belong to individual callers.
        if (config.signal || config.cancelToken || config.onDownloadProgress ||
            config.onUploadProgress || config.responseType === 'stream') {
            return adapter(config);
        }

        let key: string;
        try {
            key = requestKey(config);
        } catch {
            // Unserializable custom config should still work without deduplication.
            return adapter(config);
        }

        let request = pending.get(key);
        if (!request) {
            request = adapter(config).finally(() => {
                if (pending.get(key) === request) pending.delete(key);
            });
            pending.set(key, request);
        }

        const copyResponse = (response: AxiosResponse): AxiosResponse => ({
            ...response,
            config,
            headers: { ...response.headers }
        });

        try {
            // Axios transforms each caller's response after the adapter resolves.
            return copyResponse(await request);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw AxiosError.from(error, error.code, config, error.request,
                    error.response ? copyResponse(error.response) : undefined);
            }
            throw error;
        }
    };
}
